import { Component, inject, signal, computed, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { TableModule } from 'primeng/table';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageModule } from 'primeng/message';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { ApiToken, CreateApiToken, IssuedTokenResponse } from '../../core/models';

@Component({
    selector: 'app-tokens',
    standalone: true,
    imports: [FormsModule, DatePipe, TableModule, CardModule, TagModule, ButtonModule, DialogModule, InputTextModule, ToggleSwitchModule, MessageModule],
    template: `
        <p-card header="API Tokens">
            <p-message severity="info" styleClass="mb-3">
                Issue a bearer token for a client (e.g. a Magento installation) to authenticate against the middleware.
                The secret is shown <strong>once</strong> at creation — copy it then. Disable or rotate a token here at any time; changes take effect within ~30 seconds.
            </p-message>

            <div class="flex justify-end mb-3">
                <p-button label="New token" icon="pi pi-plus" (onClick)="openCreate()" [disabled]="busy()" />
            </div>

            <p-table [value]="tokens()" [loading]="loading()" [paginator]="tokens().length > 10" [rows]="10">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Name</th>
                        <th>Token</th>
                        <th>Roles</th>
                        <th>Status</th>
                        <th>Last used</th>
                        <th>Created</th>
                        <th style="width: 16rem">Actions</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                    <tr>
                        <td>
                            <div class="font-medium">{{ row.name }}</div>
                            @if (row.description) { <div class="text-sm text-muted-color">{{ row.description }}</div> }
                        </td>
                        <td><code>{{ row.prefix }}…{{ row.last4 }}</code></td>
                        <td>
                            @for (r of row.roles; track r) { <p-tag [value]="r" severity="secondary" styleClass="mr-1" /> }
                        </td>
                        <td>
                            @if (isExpired(row)) {
                                <p-tag value="Expired" severity="warn" />
                            } @else {
                                <p-tag [value]="row.isEnabled ? 'Active' : 'Disabled'" [severity]="row.isEnabled ? 'success' : 'secondary'" />
                            }
                        </td>
                        <td>{{ row.lastUsedAt ? (row.lastUsedAt | date: 'short') : '—' }}</td>
                        <td>
                            <div>{{ row.createdAt | date: 'short' }}</div>
                            @if (row.createdBy) { <div class="text-sm text-muted-color">{{ row.createdBy }}</div> }
                        </td>
                        <td>
                            <div class="flex items-center gap-2">
                                <p-toggleswitch [ngModel]="row.isEnabled" [disabled]="busy()" (onChange)="toggle(row, $event.checked)" />
                                <p-button label="Rotate" size="small" severity="secondary" [text]="true" [disabled]="busy()" (onClick)="askRotate(row)" />
                                <p-button icon="pi pi-trash" size="small" severity="danger" [text]="true" [disabled]="busy()" (onClick)="askDelete(row)" />
                            </div>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr><td colspan="7">{{ error() ?? 'No tokens issued yet.' }}</td></tr>
                </ng-template>
            </p-table>

            @if (error() && tokens().length) {
                <p-message severity="error" styleClass="mt-3">{{ error() }}</p-message>
            }
        </p-card>

        <!-- Create -->
        <p-dialog header="Issue a new token" [(visible)]="createOpen" [modal]="true" [style]="{ width: '32rem' }">
            <div class="flex flex-col gap-3">
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Name</label>
                    <input pInputText [(ngModel)]="form.name" placeholder="e.g. Magento QA" />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Description <span class="text-muted-color">(optional)</span></label>
                    <input pInputText [(ngModel)]="form.description" placeholder="What is this token for?" />
                </div>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Roles</label>
                    <input pInputText [(ngModel)]="form.roles" placeholder="xt.api, merchant" />
                    <small class="text-muted-color">Comma-separated. Leave the default for a Magento connection.</small>
                </div>
                <div class="flex flex-col gap-1">
                    <label class="font-medium">Expires <span class="text-muted-color">(optional)</span></label>
                    <input type="date" pInputText [(ngModel)]="form.expiresAt" />
                </div>
                @if (formError()) { <p-message severity="error">{{ formError() }}</p-message> }
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="createOpen.set(false)" [disabled]="busy()" />
                <p-button label="Issue token" icon="pi pi-key" (onClick)="create()" [loading]="busy()" />
            </ng-template>
        </p-dialog>

        <!-- One-time secret reveal -->
        <p-dialog [header]="revealTitle()" [(visible)]="revealOpen" [modal]="true" [closable]="false" [style]="{ width: '38rem' }">
            <p-message severity="warn" styleClass="mb-3">
                Copy this token now — it is shown only once and cannot be retrieved again. If you lose it, rotate the token to issue a new secret.
            </p-message>
            <div class="flex gap-2 items-center">
                <input pInputText [value]="revealed()" readonly class="flex-1 font-mono" />
                <p-button [label]="copied() ? 'Copied' : 'Copy'" [icon]="copied() ? 'pi pi-check' : 'pi pi-copy'" (onClick)="copy()" />
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Done" (onClick)="closeReveal()" />
            </ng-template>
        </p-dialog>

        <!-- Confirm (rotate / delete) -->
        <p-dialog [header]="confirm().title" [(visible)]="confirmOpen" [modal]="true" [style]="{ width: '30rem' }">
            <p>{{ confirm().message }}</p>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="confirmOpen.set(false)" [disabled]="busy()" />
                <p-button [label]="confirm().action" [severity]="confirm().danger ? 'danger' : 'primary'" (onClick)="confirm().run()" [loading]="busy()" />
            </ng-template>
        </p-dialog>
    `
})
export class Tokens implements OnInit {
    private readonly api = inject(MiddlewareApi);

    readonly tokens = signal<ApiToken[]>([]);
    readonly loading = signal(true);
    readonly busy = signal(false);
    readonly error = signal<string | null>(null);

    // Create dialog
    readonly createOpen = signal(false);
    readonly formError = signal<string | null>(null);
    form: { name: string; description: string; roles: string; expiresAt: string } = this.blankForm();

    // Reveal dialog
    readonly revealOpen = signal(false);
    readonly revealed = signal('');
    readonly copied = signal(false);
    readonly revealTitle = signal('Token issued');

    // Confirm dialog
    readonly confirmOpen = signal(false);
    readonly confirm = signal<{ title: string; message: string; action: string; danger: boolean; run: () => void }>({
        title: '', message: '', action: '', danger: false, run: () => {}
    });

    ngOnInit(): void {
        this.reload();
    }

    openCreate(): void {
        this.form = this.blankForm();
        this.formError.set(null);
        this.createOpen.set(true);
    }

    create(): void {
        if (!this.form.name.trim()) {
            this.formError.set('Name is required.');
            return;
        }
        const body: CreateApiToken = {
            name: this.form.name.trim(),
            description: this.form.description.trim() || null,
            roles: this.parseRoles(this.form.roles),
            expiresAt: this.form.expiresAt ? new Date(this.form.expiresAt).toISOString() : null
        };
        this.busy.set(true);
        this.formError.set(null);
        this.api.createApiToken(body).subscribe({
            next: (r) => this.onIssued(r, 'Token issued'),
            error: (e) => { this.formError.set(this.describe(e)); this.busy.set(false); }
        });
    }

    askRotate(row: ApiToken): void {
        this.confirm.set({
            title: 'Rotate token',
            message: `Rotate "${row.name}"? The current secret stops working immediately and a new one is issued. You'll need to update the client with the new secret.`,
            action: 'Rotate',
            danger: false,
            run: () => this.rotate(row)
        });
        this.confirmOpen.set(true);
    }

    private rotate(row: ApiToken): void {
        this.busy.set(true);
        this.api.rotateApiToken(row.id).subscribe({
            next: (r) => { this.confirmOpen.set(false); this.onIssued(r, 'Token rotated'); },
            error: (e) => { this.error.set(this.describe(e)); this.busy.set(false); this.confirmOpen.set(false); }
        });
    }

    askDelete(row: ApiToken): void {
        this.confirm.set({
            title: 'Delete token',
            message: `Permanently delete "${row.name}"? Any client still using it will immediately fail to authenticate. This cannot be undone.`,
            action: 'Delete',
            danger: true,
            run: () => this.remove(row)
        });
        this.confirmOpen.set(true);
    }

    private remove(row: ApiToken): void {
        this.busy.set(true);
        this.api.deleteApiToken(row.id).subscribe({
            next: () => { this.confirmOpen.set(false); this.reload(); },
            error: (e) => { this.error.set(this.describe(e)); this.busy.set(false); this.confirmOpen.set(false); }
        });
    }

    toggle(row: ApiToken, enabled: boolean): void {
        this.busy.set(true);
        this.error.set(null);
        this.api.setApiTokenEnabled(row.id, enabled).subscribe({
            next: () => this.reload(),
            error: (e) => { this.error.set(this.describe(e)); this.busy.set(false); this.reload(); }
        });
    }

    copy(): void {
        navigator.clipboard?.writeText(this.revealed()).then(
            () => this.copied.set(true),
            () => this.copied.set(false)
        );
    }

    closeReveal(): void {
        this.revealOpen.set(false);
        this.revealed.set('');
        this.copied.set(false);
    }

    isExpired(row: ApiToken): boolean {
        return !!row.expiresAt && new Date(row.expiresAt).getTime() <= Date.now();
    }

    private onIssued(r: IssuedTokenResponse, title: string): void {
        this.busy.set(false);
        this.createOpen.set(false);
        this.revealTitle.set(title);
        this.revealed.set(r.token);
        this.copied.set(false);
        this.revealOpen.set(true);
        this.reload();
    }

    private reload(): void {
        this.loading.set(true);
        this.api.getApiTokens().subscribe({
            next: (d) => { this.tokens.set(d ?? []); this.loading.set(false); this.busy.set(false); },
            error: (e) => { this.error.set(this.describe(e)); this.loading.set(false); this.busy.set(false); }
        });
    }

    private parseRoles(raw: string): string[] | null {
        const roles = raw.split(',').map((r) => r.trim()).filter((r) => r.length > 0);
        return roles.length ? roles : null; // null -> server applies the default (xt.api + merchant)
    }

    private blankForm() {
        return { name: '', description: '', roles: 'xt.api, merchant', expiresAt: '' };
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API (is it running, and is CORS allowed?).';
        if (e?.status === 401) return 'Unauthorized — sign in first.';
        if (e?.status === 403) return 'Forbidden — your role lacks the tokens:manage permission.';
        if (e?.status === 400) return e?.error?.error ?? 'Invalid request.';
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
